/**
 * Service for managing card number counter operations in Firestore
 */

import {
  doc,
  getDoc,
  setDoc,
  runTransaction,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { db } from "./db";
import {
  CardNumberCounter,
  createCounterFromCardNumber,
  createDefaultCounter,
  generateSafeNextNumber,
  updateCounterIfHigher,
  isAutoIncrementable,
  isValidShopId,
} from "@/lib/cardNumberUtils";

/**
 * Get the counter document path for a shop
 */
const getCounterPath = (shopId: string) =>
  `shops/${shopId}/misc/cardNumberCounter`;

/**
 * Converter for CardNumberCounter to handle Firestore timestamps
 */
const counterConverter = {
  toFirestore: (counter: CardNumberCounter) => ({
    ...counter,
    lastUpdated: serverTimestamp(),
  }),
  fromFirestore: (snapshot: any) => {
    const data = snapshot.data();
    return {
      ...data,
      lastUpdated: data.lastUpdated?.toDate?.() || new Date(),
    } as CardNumberCounter;
  },
};

/**
 * Retrieves the current card number counter for a shop
 * @param shopId - The shop ID
 * @returns Promise resolving to the counter or null if not found
 */
export const getCardNumberCounter = async (
  shopId: string
): Promise<CardNumberCounter | null> => {
  try {
    const counterRef = doc(db, getCounterPath(shopId));
    const counterSnap = await getDoc(counterRef);

    if (counterSnap.exists()) {
      const data = counterSnap.data();
      return {
        ...data,
        lastUpdated:
          data.lastUpdated instanceof Timestamp
            ? data.lastUpdated.toDate()
            : data.lastUpdated,
      } as CardNumberCounter;
    }

    return null;
  } catch (error) {
    console.error("Error getting card number counter:", error);
    return null;
  }
};

/**
 * Gets the next available card number for a shop
 * @param shopId - The shop ID
 * @returns Promise resolving to the next card number string
 */
export const getNextCardNumber = async (shopId: string): Promise<string> => {
  if (!isValidShopId(shopId)) {
    console.error("Invalid shop ID provided:", shopId);
    return "1";
  }

  try {
    const counter = await getCardNumberCounter(shopId);

    if (!counter) {
      // No counter exists, start with "1"
      return "1";
    }

    return generateSafeNextNumber(counter);
  } catch (error) {
    console.error("Error getting next card number:", error);
    return "1"; // Fallback to "1"
  }
};

/**
 * Updates the card number counter with a new card number using a transaction
 * This ensures thread-safe updates when multiple users create cards simultaneously
 * @param shopId - The shop ID
 * @param cardNumber - The card number to update the counter with
 * @returns Promise resolving to the updated counter or null if the card number is invalid
 */
export const updateCardNumberCounter = async (
  shopId: string,
  cardNumber: string
): Promise<CardNumberCounter | null> => {
  if (!isAutoIncrementable(cardNumber)) {
    // Skip non-numeric card numbers
    return null;
  }

  try {
    const counterRef = doc(db, getCounterPath(shopId));

    return await runTransaction(db, async (transaction) => {
      const counterDoc = await transaction.get(counterRef);

      let currentCounter: CardNumberCounter;

      if (counterDoc.exists()) {
        const data = counterDoc.data();
        currentCounter = {
          ...data,
          lastUpdated:
            data.lastUpdated instanceof Timestamp
              ? data.lastUpdated.toDate()
              : data.lastUpdated,
        } as CardNumberCounter;
      } else {
        // No counter exists, create a default one
        currentCounter = createDefaultCounter(shopId);
      }

      // Update counter if the new card number is higher
      const updatedCounter = updateCounterIfHigher(currentCounter, cardNumber);

      // Only write if the counter was actually updated
      if (updatedCounter.numericValue > currentCounter.numericValue) {
        transaction.set(
          counterRef,
          counterConverter.toFirestore(updatedCounter)
        );
        return {
          ...updatedCounter,
          lastUpdated: new Date(), // Use current date since serverTimestamp() resolves later
        };
      }

      return currentCounter;
    });
  } catch (error) {
    console.error("Error updating card number counter:", error);
    return null;
  }
};

/**
 * Initializes the card number counter from existing card numbers in the shop
 * This is useful for setting up the counter for shops with existing cards
 * @param shopId - The shop ID
 * @param existingCardNumbers - Array of existing card numbers to analyze
 * @returns Promise resolving to the initialized counter
 */
export const initializeCounterFromExistingCards = async (
  shopId: string,
  existingCardNumbers: string[]
): Promise<CardNumberCounter | null> => {
  try {
    let highestCounter: CardNumberCounter | null = null;

    // Find the highest valid card number
    for (const cardNumber of existingCardNumbers) {
      const counter = createCounterFromCardNumber(cardNumber, shopId);
      if (
        counter &&
        (!highestCounter || counter.numericValue > highestCounter.numericValue)
      ) {
        highestCounter = counter;
      }
    }

    if (!highestCounter) {
      highestCounter = createDefaultCounter(shopId);
    }

    // Set the counter in Firestore
    const counterRef = doc(db, getCounterPath(shopId));
    await setDoc(counterRef, counterConverter.toFirestore(highestCounter));

    return {
      ...highestCounter,
      lastUpdated: new Date(),
    };
  } catch (error) {
    console.error("Error initializing card number counter:", error);
    return null;
  }
};

/**
 * Resets the card number counter to a specific value
 * This is useful for admin operations or when manual adjustment is needed
 * @param shopId - The shop ID
 * @param cardNumber - The card number to reset the counter to
 * @returns Promise resolving to the reset counter or null if invalid
 */
export const resetCardNumberCounter = async (
  shopId: string,
  cardNumber: string
): Promise<CardNumberCounter | null> => {
  const counter = createCounterFromCardNumber(cardNumber, shopId);

  if (!counter) {
    return null;
  }

  try {
    const counterRef = doc(db, getCounterPath(shopId));
    await setDoc(counterRef, counterConverter.toFirestore(counter));

    return {
      ...counter,
      lastUpdated: new Date(),
    };
  } catch (error) {
    console.error("Error resetting card number counter:", error);
    return null;
  }
};
